import { gql, useMutation, useQuery } from "@apollo/client";
import {
  IconButton,
  Menu,
  Popover,
  Position,
  Table,
  TextDropdownButton,
  toaster,
  MoreIcon,
  Tooltip,
  Button,
  AddIcon,
  SideSheet,
  CrossIcon,
  TextInputField,
  ExportIcon,
} from "evergreen-ui";
import React, { useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Refresh from "src/Components/Refresh";
import UserSelector from "src/Components/UserSelector";
import {
  CASH_WALLET_RECORDS_FRAGMENT,
  CASH_WITHDRAWAL_FRAGMENT,
} from "src/fragments";
import useInput from "src/Hooks/useInput";
import { exportToCsv, getDateWithTime } from "src/utilities";
import styled from "styled-components";

const GET_RECORDS = gql`
  query getWithdrawalRecords($filter: cash_wallet_records_bool_exp) {
    cash_wallet_records(where: $filter, order_by: { id: desc }) {
      ...CashWalletRecordsPart
    }
  }
  ${CASH_WALLET_RECORDS_FRAGMENT}
`;

const UPDATE_WITHDRAWAL_REQUEST = gql`
  mutation updateWithdrawal($id: Int!, $status: String!) {
    update_cash_balance(
      where: { id: { _eq: $id } }
      _set: { status: $status }
    ) {
      returning {
        ...CashWithdrawalPart
      }
    }
  }
  ${CASH_WITHDRAWAL_FRAGMENT}
`;

const INSERT_WITHDRAWAL_REQUEST = gql`
  mutation insertRecord($object: cash_balance_insert_input!) {
    insert_cash_balance_one(object: $object) {
      ...CashWithdrawalPart
    }
  }
  ${CASH_WITHDRAWAL_FRAGMENT}
`;

const Title = styled.h1`
  ${(props) => props.theme.title}
`;

interface IProps extends RouteComponentProps<any> {}
const AdminCashWithdrawal: React.FC<IProps> = ({ history, match }) => {
  let searchType = match.params.cash_type;
  const searchTerm = useInput("");
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  let searchCondition = { _like: `${searchTerm.value}` };
  const [showBox, setShowBox] = useState(false);
  const [amount, setAmount] = useState(0);
  const [message, setMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const { data, loading, error, refetch } = useQuery(GET_RECORDS, {
    variables: {
      filter: {
        _and: [
          Boolean(searchType)
            ? {
                type: {
                  _eq: searchType,
                },
              }
            : {},
          status === ""
            ? {}
            : {
                status: { _eq: status },
              },
          searchTerm.value === ""
            ? {}
            : {
                _or: [
                  { user: { username: searchCondition } },
                  { user: { name: searchCondition } },
                  { user: { id: searchCondition } },
                ],
              },
        ],
      },
    },
  });

  const [updateWithdrawal, { loading: mutationLoading }] = useMutation(
    UPDATE_WITHDRAWAL_REQUEST
  );

  const [
    newRecord,
    { loading: insertLoading, error: insertError },
  ] = useMutation(INSERT_WITHDRAWAL_REQUEST);
  const refresh = () => {
    setIsLoading(true);
    refetch().then(() => {
      setIsLoading(false);
    });
  };
  const onSubmit = (id, status) => {
    if (!mutationLoading) {
      updateWithdrawal({
        variables: {
          id,
          status,
        },
      })
        .then(() => {
          refresh();
        })
        .catch((e) => {
          toaster.danger(e.message);
        });
    } else {
      toaster.danger("A previous request is still loading...");
    }
  };

  const insertNewRecord = () => {
    if (!insertLoading && selectedUser) {
      newRecord({
        variables: {
          object: {
            add: amount > 0,
            amount,
            description: message,
            type: "system_insert",
            user_id: selectedUser,
          },
        },
      })
        .then(() => {
          toaster.success("Request succeed!");
          refetch().then(() => {
            setShowBox(false);
          });
        })
        .catch((e) => {
          toaster.danger(
            e.message === "database query error"
              ? "User Balance insufficient."
              : e.message
          );
          console.log(e);
        });
    }
  };

  const exportData = (e) => {
    e.preventDefault();
    let csv = "ID,NAME,USERNAME,CREATED DATE,AMOUNT,STATUS,TYPE,ITEM,OPENING BALANCE,CLOSING BALANCE\n";
    data.cash_wallet_records.map((cash) => {
      return (csv =
        csv +
        `${cash.id},${cash.user.name},${cash.user.username},${getDateWithTime(
          cash.created_at
        )},${cash.amount},${cash.status}, ${cash.type}, ${cash.details},${cash.opening_balance || "N/A"}, ${cash.closing_balance || "N/A"}\n`);
    });

    exportToCsv(csv, "CASH_WALLET");
    toaster.success("Data exported!");
  };

  return (
    <div className=" admin-page-container">
      <div className=" w-full flex flex-col md:flex-row items-center justify-between pb-3 px-2">
        <Title className=" flex flex-row items-center">
          Cash withdrawal requests
          <span>
            <Refresh onClick={refresh} loading={isLoading} />
          </span>
        </Title>
        <div>
          <Button
            iconBefore={AddIcon}
            appearance="minimal"
            onClick={() => {
              setShowBox(true);
            }}
          >
            New Record
          </Button>
          <Button
            iconBefore={ExportIcon}
            appearance="minimal"
            onClick={exportData}
          >
            Export to Excel
          </Button>
        </div>
      </div>
      <Table
        marginBottom={"1rem"}
        maxHeight={"90vh"}
        width="100%"
        maxWidth="calc(100vw - 2rem)"
        overflow="scroll"
      >
        <Table.Head minWidth="1080px" accountForScrollbar={true}>
          <Table.SearchHeaderCell
            placeholder="Search by name, username, id"
            {...searchTerm}
          />
          <Table.TextHeaderCell>Created at</Table.TextHeaderCell>
          <Table.TextHeaderCell>Amount</Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Order"
                    options={[
                      { label: "Approved", value: "Approved" },
                      { label: "Pending", value: "Pending" },
                      { label: "Rejected", value: "Rejected" },
                      { label: "None", value: "" },
                    ]}
                    onChange={(value) => {
                      setStatus(value);
                      close();
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {status === "" ? "Status" : status}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell>Item</Table.TextHeaderCell>
          <Table.TextHeaderCell>Opening Balance</Table.TextHeaderCell>
          <Table.TextHeaderCell>Closing Balance</Table.TextHeaderCell>
          <Table.TextHeaderCell>Action</Table.TextHeaderCell>
        </Table.Head>
        <Table.Body
          marginBottom={"1rem"}
          maxHeight={"90vh"}
          minWidth="1080px"
          overflow="scroll"
        >
          {data &&
            !loading &&
            (data.cash_wallet_records.length > 0
              ? data.cash_wallet_records.map((rec, index) => (
                  <Table.Row key={rec.table_name + rec.id}>
                    <Table.Cell>
                      <Tooltip
                        content={
                          <div className=" p-2 bg-white flex flex-col">
                            <p className=" font-semibold mb-4">Bank account</p>
                            {rec?.user?.user_banks?.map((bank, index) => (
                              <div
                                className=" border-b border-gray-200"
                                key={index}
                              >
                                <p className=" mt-2">
                                  Bank Account No:{" "}
                                  <span className=" font-bold">
                                    {bank.bank_acc_no}
                                  </span>
                                </p>
                                <p className=" mt-2">
                                  Bank Account Holder:{" "}
                                  <span className=" font-bold">
                                    {bank.bank_acc_name}
                                  </span>
                                </p>
                                <p className=" mt-2">
                                  Bank Name:{" "}
                                  <span className=" font-bold">
                                    {bank.bank_name}
                                  </span>
                                </p>
                              </div>
                            ))}
                          </div>
                        }
                        appearance="card"
                      >
                        <p
                          onClick={() => {
                            history.push("/admin/agents/" + rec.user.username);
                          }}
                          className=" underline text-blue-500 cursor-pointer"
                        >
                          {rec.user.name} ({rec.user.username})
                        </p>
                      </Tooltip>
                    </Table.Cell>
                    <Table.TextCell>
                      {getDateWithTime(rec.created_at)}
                    </Table.TextCell>
                    <Table.TextCell>
                      {rec.user.user_country.currency}
                      {rec.amount}
                    </Table.TextCell>
                    <Table.TextCell>{rec.status}</Table.TextCell>
                    <Table.TextCell>{rec.details}</Table.TextCell>
                    <Table.TextCell>{rec.opening_balance}</Table.TextCell>
                    <Table.TextCell>{rec.closing_balance}</Table.TextCell>
                    <Table.Cell>
                      <Popover
                        content={
                          <Menu.Group title="Update Status">
                            <Menu.Item
                              onSelect={() => {
                                onSubmit(rec.id, "Approved");
                              }}
                              disabled={
                                loading ||
                                mutationLoading ||
                                rec.status !== "Pending"
                              }
                            >
                              {loading ? "Loading..." : "Approve"}
                            </Menu.Item>
                            <Menu.Item
                              onSelect={() => {
                                onSubmit(rec.id, "Rejected");
                              }}
                              disabled={
                                loading ||
                                mutationLoading ||
                                rec.status !== "Pending"
                              }
                            >
                              {loading ? "Loading..." : "Reject"}
                            </Menu.Item>
                          </Menu.Group>
                        }
                      >
                        <IconButton
                          icon={MoreIcon}
                          height={24}
                          appearance="minimal"
                        />
                      </Popover>
                    </Table.Cell>
                  </Table.Row>
                ))
              : "No record found!")}
        </Table.Body>
      </Table>
      <SideSheet
        isShown={showBox}
        onCloseComplete={() => {
          setShowBox(false);
        }}
      >
        <div className=" w-screen sm:w-full bg-gray-50 h-full">
          <div className=" w-full bg-white p-3 flex flex-row justify-between items-center">
            <h1 className=" text-gray-600 font-bold ">
              New Cash Wallet Record
            </h1>
            <IconButton
              icon={CrossIcon}
              onClick={() => {
                setShowBox(false);
              }}
            />
          </div>
          <div className=" w-full p-3 border-t border-solid border-gray-200">
            <UserSelector
              label="Agent Username"
              onSelect={(selectedUser) => {
                setSelectedUser(selectedUser);
              }}
            />
            <TextInputField
              value={amount}
              label="Amount"
              onChange={(e) => {
                setAmount(e.target.value);
              }}
              hint="The amount you enter is the amount user will receive. For withdrawal, use negative amount. (e.g. -300)"
            />
            <TextInputField
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              hint="Leave a message so users know what this is about!"
              label="Message"
            />
            <div className=" w-full py-3">
              <Button
                width="100%"
                appearance="primary"
                disabled={insertLoading}
                onClick={insertNewRecord}
              >
                Save
              </Button>
            </div>
            {insertError?.message === "database query error" &&
              "User Balance insufficient."}
          </div>
        </div>
      </SideSheet>
      {!loading && error && <p>{error?.message}</p>}
    </div>
  );
};

export default withRouter(AdminCashWithdrawal);
